import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DataTable from 'src/components/ui/data-table';
import FilterButton from 'src/components/common/FilterButton';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Checkbox } from 'src/components/ui/checkbox';
import { Briefcase, ArrowLeft } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import AppSelect from 'src/components/common/AppSelect';
import { format } from 'date-fns';

// Mock partners data
const mockPartners = [
  { id: 1, name: 'Partner A' },
  { id: 2, name: 'Partner B' },
  { id: 3, name: 'Partner C' },
];

// Mock data for candidates
const mockCandidates = {
  submitted: [
    {
      id: 1,
      candidate_id: 1,
      name: 'Shriya Dua',
      email: 'shriya.dua135@gmail.com',
      phone: '+919943395898',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 1,
    },
    {
      id: 2,
      candidate_id: 2,
      name: 'Meera Sachdeva',
      email: 'meera.sachdeva136@gmail.com',
      phone: '+917667282976',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 1,
    },
    {
      id: 3,
      candidate_id: 3,
      name: 'Rhea Dhawan',
      email: 'rhea.dhawan138@gmail.com',
      phone: '+919976426985',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 2,
    },
    {
      id: 4,
      candidate_id: 4,
      name: 'Vani Talwar',
      email: 'vani.talwar139@gmail.com',
      phone: '+919176209706',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 2,
    },
    {
      id: 5,
      candidate_id: 5,
      name: 'Karthik Vohra',
      email: 'karthik.vohra141@gmail.com',
      phone: '+919905719486',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 3,
    },
    {
      id: 6,
      candidate_id: 6,
      name: 'Pramod Batra',
      email: 'pramod.batra143@gmail.com',
      phone: '+919727089825',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 3,
    },
    {
      id: 7,
      candidate_id: 7,
      name: 'Sachin Mahajan',
      email: 'sachin.mahajan145@gmail.com',
      phone: '9833560740',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 1,
    },
    {
      id: 8,
      candidate_id: 8,
      name: 'Tarun Saluja',
      email: 'tarun.saluja146@gmail.com',
      phone: '9820215221',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 2,
    },
    {
      id: 9,
      candidate_id: 9,
      name: 'Yogesh Sharma',
      email: 'yogesh.sharma148@gmail.com',
      phone: '8848207264',
      status: 'Submitted',
      updated_on: '2026-01-09',
      partner_id: 3,
    },
  ],
  success: [],
  failed: [],
  duplicate: [],
};

// Mock requirement data
const mockRequirementData = {
  id: 1,
  registration_id: 'REQ-001',
  title: 'Relationship Manager - Business Banking Risk and Compliance Focus',
  location: 'Hyderabad',
  employment_type: 'Full-Time',
  organization: 'Acme Bank',
  submitted_count: 9,
  success_count: 0,
  failed_count: 0,
  dedupe_count: 0,
};

const DEFAULT_PAGE_SIZE = 10;

const RequirementSubmissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentFilter, setCurrentFilter] = useState(
    searchParams.get('status') || 'submitted'
  );
  const [selectedPartner, setSelectedPartner] = useState(
    searchParams.get('partner') || 'all'
  );
  const [selectedCandidates, setSelectedCandidates] = useState([]);

  // Get current page from URL
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  // TODO: Replace with actual API call
  const requirementData = mockRequirementData;
  const allCandidates = mockCandidates[currentFilter] || [];

  // Filter candidates by selected partner
  const candidates = useMemo(() => {
    if (selectedPartner === 'all') {
      return allCandidates;
    }
    return allCandidates.filter(
      (candidate) => candidate.partner_id?.toString() === selectedPartner
    );
  }, [allCandidates, selectedPartner]);

  // Filter tabs configuration
  const filters = [
    {
      label: 'Submitted',
      value: 'submitted',
      statsLabel: 'submitted_count',
      activeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
    },
    {
      label: 'Success',
      value: 'success',
      statsLabel: 'success_count',
      activeClass: 'bg-green-100 text-green-800 border border-green-300',
    },
    {
      label: 'Failed',
      value: 'failed',
      statsLabel: 'failed_count',
      activeClass: 'bg-red-100 text-red-800 border border-red-300',
    },
    {
      label: 'Duplicate',
      value: 'duplicate',
      statsLabel: 'dedupe_count',
      activeClass: 'bg-orange-100 text-orange-800 border border-orange-300',
    },
  ];

  const countStats = {
    submitted_count: requirementData.submitted_count,
    success_count: requirementData.success_count,
    failed_count: requirementData.failed_count,
    dedupe_count: requirementData.dedupe_count,
  };

  const handleFilterChange = (filterValue) => {
    setCurrentFilter(filterValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('status', filterValue);
    newParams.set('page', '1'); // Reset to first page on filter change
    setSearchParams(newParams);
    setSelectedCandidates([]); // Clear selection on filter change
  };

  const handlePartnerChange = (partnerId) => {
    setSelectedPartner(partnerId);
    const newParams = new URLSearchParams(searchParams);
    if (partnerId === 'all') {
      newParams.delete('partner');
    } else {
      newParams.set('partner', partnerId);
    }
    newParams.set('page', '1'); // Reset to first page on partner change
    setSearchParams(newParams);
    setSelectedCandidates([]); // Clear selection on partner change
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates((prev) => {
      if (prev.includes(candidateId)) {
        return prev.filter((itemId) => itemId !== candidateId);
      }
      return [...prev, candidateId];
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCandidates(candidates.map((c) => c.candidate_id));
    } else {
      setSelectedCandidates([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const allSelected =
    candidates.length > 0 &&
    candidates.every((c) => selectedCandidates.includes(c.candidate_id));

  const selectHeaderNode = (
    <div className="flex items-center ml-2 w-full h-full">
      <Checkbox
        checked={allSelected}
        onCheckedChange={(val) => handleSelectAll(Boolean(val))}
      />
    </div>
  );

  const columns = useMemo(
    () => [
      {
        key: 'select',
        header: selectHeaderNode,
        width: '48px',
        minWidth: '48px',
        className: 'w-12 text-center relative overflow-hidden',
        align: 'center',
        render: (_v, row) => (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            data-prevent-row-click="true"
            role="button"
            tabIndex={-1}
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
          >
            <Checkbox
              checked={selectedCandidates.includes(row.candidate_id)}
              onCheckedChange={() => handleSelectCandidate(row.candidate_id)}
            />
          </div>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        className: 'font-semibold',
        render: (_v, row) => row.name,
      },
      {
        key: 'email',
        header: 'Email',
        sortable: true,
        tooltip: true,
        render: (_v, row) => row.email,
      },
      {
        key: 'phone',
        header: 'Phone',
        sortable: true,
        render: (_v, row) => row.phone,
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (_v, row) => row.status,
      },
      {
        key: 'updated_on',
        header: 'Updated on',
        sortable: true,
        render: (_v, row) => formatDate(row.updated_on),
      },
    ],
    [selectedCandidates, allSelected]
  );

  const handlePageChange = (newPage) => {
    const page = parseInt(newPage, 10);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const handlePageSizeChange = (newPageSize) => {
    const size = parseInt(newPageSize, 10);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageSize', size.toString());
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // Calculate pagination
  const totalItems = candidates.length;
  const totalPages = Math.ceil(totalItems / currentPageSize);
  const startIndex = (currentPage - 1) * currentPageSize;
  const endIndex = startIndex + currentPageSize;
  const paginatedCandidates = candidates.slice(startIndex, endIndex);

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Source Candidate', href: '/source-candidates' },
    {
      label: requirementData.title,
      href: `/source-candidates/${id}`,
    },
  ];

  return (
    <div className="text-left bg-[#F8FAFC] min-h-screen">
      <PageHeader
        breadcrumbs={breadcrumbItems}
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={() => navigate('/source-candidates')}
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
            Back
          </Button>
        }
      />

      <PageContainer className="p-4 space-y-4">
        {/* Header Section */}
        <div className="bg-card rounded-lg shadow-sm p-6 border border-blue-200 dark:border-blue-900/40">
          <div className="flex items-start gap-3">
            <span className="p-2 bg-blue-50 rounded-md">
              <Briefcase className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            </span>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground-strong mb-2">
                {requirementData.title}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                All candidates submitted for this requirement are listed below.
                Use the status tabs to filter.
              </p>
            </div>
          </div>
        </div>

        {/* Partner Filter and Status Tabs */}
        <div className="space-y-4 flex items-center justify-between">
          <div className="p-2 w-full flex items-center justify-between">
            <FilterButton
              filters={filters}
              defaultActiveFilter={currentFilter}
              onFilterChange={handleFilterChange}
              countStats={countStats}
            />

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <AppSelect
                  value={selectedPartner}
                  onValueChange={handlePartnerChange}
                  options={[
                    { value: 'all', label: 'All' },
                    ...mockPartners.map((p) => ({
                      value: p.id.toString(),
                      label: p.name,
                    })),
                  ]}
                  placeholder="Select Partner"
                  className="w-[200px]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-card rounded-lg shadow-sm">
          <DataTable
            data={paginatedCandidates}
            columns={columns}
            loading={false}
            pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={currentPageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="border-blue-200"
            emptyState="No candidates found."
          />
        </div>
      </PageContainer>
    </div>
  );
};

export default RequirementSubmissions;
